"use client";
import { buttonVariants } from "@/components/ui/button";
import { handleParse } from "@/lib/handleParse";
import { cn } from "@/lib/utils";
import { FeaturedImage } from "@/types/page";
import { motion } from "framer-motion";
import { ArrowRightIcon, StarIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import NextDate from "./NextDate";

const fadeContainer = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3, // Stagger each child (title, description, buttons)
    },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 44 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const fadeUpButton = {
  hidden: { opacity: 0, y: 20, scale: 0.95 }, // Slightly smaller and lower
  visible: {
    opacity: 1,
    y: 0,
    scale: 1, // Grow effect
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

export default function HomeHero({
  img,
  description,
  title,
}: {
  img: FeaturedImage | undefined;
  description: string | undefined;
  title: string | undefined;
}) {
  return (
    <div className="relative w-full md:h-[802px] h-[550px] contain">
      <div className="absolute inset-0">
        {img?.node?.mediaItemUrl && img?.node?.altText && (
          <div className="relative w-full h-full">
            <Image
              src={img.node.mediaItemUrl}
              alt={img.node.altText || ""}
              fill
              priority
              quality={75}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
              style={{
                objectFit: "cover",
                objectPosition: "center",
              }}
              placeholder="blur"
              blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4gHYSUNDX1BST0ZJTEUAAQEAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADb/2wBDABQODxIPDRQSEBIXFRQdHx4eHRoaHSQtJSEkMjU1LS0yMi4qLjgyPj4+OD5AQEBAR0dHSEhISEtISEhISEhISEj/2wBDAR4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh7/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAb/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
            />
          </div>
        )}
      </div>
      <div className="absolute z-10 inset-0 bg-overlay hidden md:block"></div>
      <div className="absolute z-10 inset-0 bg-black/40 block md:hidden"></div>
      <div className="absolute w-full z-20 top-[65px] h-[calc(100%-65px)] layout bg-transparent md:grid md:grid-cols-2 font-josefin text-white md:py-24 py-10">
        <div className="flex flex-col justify-between ">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeContainer} // Apply stagger effect
          >
            {/* Title */}
            <motion.h1
              variants={fadeUp}
              className="text-3xl md:text-5xl lg:text-6xl 2xl:text-7xl font-bold font-josefin uppercase"
            >
              {title}
            </motion.h1>

            {/* Description */}
            <motion.div variants={fadeUp}>
              {description && handleParse(description)}
            </motion.div>

            {/* Buttons */}
            <motion.div className="flex flex-col md:flex-row pt-8 gap-4">
              <motion.div variants={fadeUpButton}>
                <Link
                  className={cn(
                    buttonVariants({
                      variant: "outlineGold",
                      effect: "expandIcon",
                      size: "lg",
                      className: "w-full h-[48px] sm:w-fit",
                    })
                  )}
                  href="/vara-provningar"
                >
                  Se alla provningar{" "}
                  <div className="w-0 translate-x-[100%] pl-0 opacity-0 transition-all duration-200 group-hover:w-5 group-hover:translate-x-0 group-hover:pl-2 group-hover:opacity-100">
                    <ArrowRightIcon />
                  </div>
                </Link>
              </motion.div>

              <motion.div variants={fadeUpButton}>
                <Link
                  className={cn(
                    buttonVariants({
                      effect: "expandIcon",
                      size: "lg",
                      className: "w-full h-[48px] sm:w-fit",
                    })
                  )}
                  href="/produkter/presentkort"
                >
                  Ge bort ett presentkort
                  <div className="w-0 translate-x-[100%] pl-0 opacity-0 transition-all duration-200 group-hover:w-5 group-hover:translate-x-0 group-hover:pl-2 group-hover:opacity-100">
                    <ArrowRightIcon />
                  </div>
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>

          <NextDate />
        </div>

        <div className="lg:flex lg:justify-end lg:items-end hidden">
          <div className="flex gap-4 backdrop-blur-sm bg-primary-pink/30 border border-white/30 text-[#333] p-4 rounded-lg font-josefin font-bold shadow-lg">
            <div className="border-r-2 border-white/80 pr-4  items-center">
              <FiveStars />
              <p>1 000+ vinprovningar</p>
            </div>
            <div className="border-r-2 border-white/80 pr-4  items-center">
              <FiveStars />
              <p>Erfarna sommelierer</p>
            </div>
            <div>
              <FiveStars />
              <p>Noga utvalda drycker</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const FiveStars = () => {
  return (
    <div className="flex gap-1 pb-1">
      {[...Array(5)].map((_, i) => (
        <StarIcon
          key={i}
          className="text-primary-red fill-primary-red size-4"
        />
      ))}
    </div>
  );
};
